import { useCallback, useEffect, useRef, useState } from 'react';
import { createPanelNavigation, type MainPanel } from '../utils/panelNavigation';

export function usePanelNavigation(allowed: boolean) {
  const [panel, setPanel] = useState<MainPanel | null>(null);
  const allowedRef = useRef(allowed);
  allowedRef.current = allowed;
  const controllerRef = useRef<ReturnType<typeof createPanelNavigation> | null>(null);

  useEffect(() => {
    const mobile = window.matchMedia('(max-width: 767px)');
    const controller = createPanelNavigation({ history: window.history,
      href: () => window.location.href, isMobile: () => mobile.matches,
      isAllowed: () => allowedRef.current, onChange: setPanel, owner: crypto.randomUUID() });
    controllerRef.current = controller;
    const sync = () => controller.syncFromHistory();
    const refresh = () => controller.refresh();
    window.addEventListener('popstate', sync);
    mobile.addEventListener('change', refresh);
    controller.refresh();
    return () => {
      window.removeEventListener('popstate', sync);
      mobile.removeEventListener('change', refresh);
      controller.dispose();
      controllerRef.current = null;
    };
  }, []);

  useEffect(() => { controllerRef.current?.refresh(); }, [allowed]);
  const openPanel = useCallback((next: MainPanel) => controllerRef.current?.open(next), []);
  const closePanel = useCallback(() => controllerRef.current?.close(), []);
  const navigateAfterPanel = useCallback((navigation: () => void) => {
    if (controllerRef.current) controllerRef.current.beforeNavigate(navigation);
    else navigation();
  }, []);
  return { panel, openPanel, closePanel, navigateAfterPanel };
}
